import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, TrendingDown, Users, Calendar, CreditCard, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/widgets/StatCard';
import { TrendLineChart } from '@/components/charts/TrendLineChart';
import { useBillingOverview } from '@/hooks/useBilling';

const formatCurrency = (amount: number, currency: string = 'BRL') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const BillingOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d');
  const { data: billing, isLoading, refetch } = useBillingOverview(period);

  // Prepare chart data for MRR trend
  const chartData = billing?.mrrHistory?.map((item) => ({
    date: new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    MRR: item.mrr,
  })) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Metricas financeiras e receita recorrente
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={() => refetch()}
          >
            Atualizar
          </Button>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={[
              { value: '7d', label: 'Ultimos 7 dias' },
              { value: '30d', label: 'Ultimos 30 dias' },
              { value: '90d', label: 'Ultimos 90 dias' },
              { value: '365d', label: 'Ultimo ano' },
            ]}
            className="w-48"
          />
        </div>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="MRR"
          value={formatCurrency(billing?.mrr || 0)}
          icon={<DollarSign className="w-6 h-6" />}
          trend={billing?.mrrTrend}
          description="Receita Mensal Recorrente"
        />
        <StatCard
          title="ARR"
          value={formatCurrency(billing?.arr || 0)}
          icon={<Calendar className="w-6 h-6" />}
          description="Receita Anual Recorrente"
        />
        <StatCard
          title="ARPU"
          value={formatCurrency(billing?.arpu || 0)}
          icon={<Users className="w-6 h-6" />}
          trend={billing?.arpuTrend}
          description="Receita Media por Usuario"
        />
        <StatCard
          title="Churn Rate"
          value={`${(billing?.churnRate || 0).toFixed(2)}%`}
          icon={<TrendingDown className="w-6 h-6" />}
          trend={billing?.churnTrend}
          description="Taxa de cancelamento"
        />
      </div>

      {/* MRR Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Evolucao do MRR
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <TrendLineChart
              data={chartData}
              xKey="date"
              lines={[{ key: 'MRR', color: '#10b981' }]}
              height={300}
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Dados insuficientes para o grafico
            </div>
          )}
        </CardContent>
      </Card>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Subscription Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Distribuicao por Plano</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {billing?.subscriptionsByPlan?.map((item) => (
                <div key={item.planName} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant={item.planType === 'pro' ? 'primary' : item.planType === 'enterprise' ? 'info' : 'default'}>
                      {item.planName}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">{item.count}</p>
                    <p className="text-sm text-gray-500">{formatCurrency(item.mrr)}/mes</p>
                  </div>
                </div>
              )) || (
                <p className="text-gray-500 text-center">Nenhum dado disponivel</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Novas assinaturas</span>
                <Badge variant="success">+{billing?.newSubscriptions || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Cancelamentos</span>
                <Badge variant="danger">-{billing?.canceledSubscriptions || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Upgrades</span>
                <Badge variant="primary">+{billing?.upgrades || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Downgrades</span>
                <Badge variant="warning">-{billing?.downgrades || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Revenue */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Receita Liquida</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Receita Bruta</span>
                <span className="font-semibold">{formatCurrency(billing?.grossRevenue || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Reembolsos</span>
                <span className="font-semibold text-red-600">-{formatCurrency(billing?.refunds || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Taxas (Stripe)</span>
                <span className="font-semibold text-red-600">-{formatCurrency(billing?.fees || 0)}</span>
              </div>
              <hr className="border-gray-200 dark:border-gray-700" />
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900 dark:text-white">Receita Liquida</span>
                <span className="font-bold text-green-600 dark:text-green-400 text-lg">
                  {formatCurrency(billing?.netRevenue || 0)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Historico de Eventos
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Visualize todos os eventos de pagamento, assinaturas e reembolsos
              </p>
            </div>
            <Button
              leftIcon={<CreditCard className="w-4 h-4" />}
              onClick={() => navigate('/admin/billing/events')}
            >
              Ver Eventos
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingOverviewPage;
